import { Overlay } from '@angular/cdk/overlay';
import {
  DestroyRef,
  inject,
  reflectComponentType,
  Service,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs';
import {
  DEFAULT_TOAST_ICONS,
  DEFAULT_TOAST_INTENTS,
  ONYX_TOASTS_LIMIT,
} from '../components/toast/constants';
import { OnyxToastMessage, OnyxToastIntent } from '../components/toast/enums';
import type {
  OnyxCustomToastOptions,
  OnyxToast,
  OnyxToastOptions,
} from '../components/toast/interfaces';
import {
  ONYX_TOASTER_CONFIG,
  OnyxToasterComponent,
} from '../components/toast/onyx-toaster/onyx-toaster.component';
import { I18N_NAMESPACE } from '../internal/constants';
import { OnyxBaseOverlayService } from '../internal/services/onyx-base-overlay.service';
import { OnyxPopoverService } from './onyx-popover.service';

@Service()
export class OnyxToastService {
  private readonly ONYX_TOASTER_TAG_NAME =
    reflectComponentType(OnyxToasterComponent)!.selector.toUpperCase();

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly overlayService = new OnyxBaseOverlayService();
  private readonly popoverService = inject(OnyxPopoverService);

  private toasts = signal<OnyxToast[]>([]);

  private routeChange$ = this.router.events.pipe(
    map(() => this.router.url.split('?')[0]),
    distinctUntilChanged(),
    map(() => undefined),
    takeUntilDestroyed(this.destroyRef),
  );

  constructor() {
    this.createToasterOverlay();

    this.popoverService.added$
      .pipe(
        filter((element) => !element.querySelector(this.ONYX_TOASTER_TAG_NAME)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.raiseToaster());

    this.routeChange$.subscribe(() =>
      this.toasts.update((toasts) => toasts.filter((t) => t.keepOnNavigation)),
    );
  }

  public showSuccess(options: OnyxToastOptions): OnyxToast {
    return this.showToast({
      intent: OnyxToastIntent.SUCCESS,
      ...options,
    });
  }

  public showError(options: OnyxToastOptions): OnyxToast {
    return this.showToast({
      intent: OnyxToastIntent.ERROR,
      ...options,
    });
  }

  public showWarning(options: OnyxToastOptions): OnyxToast {
    return this.showToast({
      intent: OnyxToastIntent.WARNING,
      ...options,
    });
  }

  public showInformation(options: OnyxToastOptions): OnyxToast {
    return this.showToast({
      intent: OnyxToastIntent.INFORMATION,
      ...options,
    });
  }

  public showCustom(
    type: OnyxToastMessage,
    options?: OnyxCustomToastOptions,
  ): OnyxToast {
    const toast = this.mapType(type, options);
    return this.showToast(toast);
  }

  public hideToast(toast: OnyxToast): void {
    this.toasts.update((toasts) => toasts.filter((t) => t !== toast));
  }

  public hideToasts(): void {
    this.toasts.set([]);
  }

  private showToast(toast: OnyxToast): OnyxToast {
    this.toasts.update((toasts) =>
      [toast, ...toasts].slice(0, ONYX_TOASTS_LIMIT),
    );
    return toast;
  }

  private mapType(
    messageType: OnyxToastMessage,
    options?: OnyxCustomToastOptions,
  ): OnyxToast {
    const [intent, icon] = [
      DEFAULT_TOAST_INTENTS[messageType],
      DEFAULT_TOAST_ICONS[messageType],
    ];
    const context = (() => {
      const I18N = `${I18N_NAMESPACE}.toasts`;

      switch (messageType) {
        case OnyxToastMessage.SERVER_ERROR:
          return {
            message: `${I18N}.${messageType}`,
            description: `${I18N}.description.${messageType}`,
          };

        default:
          return { message: `${I18N}.${messageType}` };
      }
    })();

    const { message, description } = context;

    return {
      intent,
      icon: options?.icon ?? icon,
      message,
      description,
      ...options,
    };
  }

  private raiseToaster(): void {
    const host = this.overlayService.overlayRef()?.hostElement;
    if (!host?.matches(':popover-open')) return;

    host.hidePopover();
    host.showPopover();
  }

  private createToasterOverlay(): void {
    const overlayRef = this.overlayService.createOverlayRef({
      positionStrategy: this.overlay.position().global(),
    });

    const componentInjector = this.overlayService.createComponentInjector(
      ONYX_TOASTER_CONFIG,
      {
        toasts: this.toasts.asReadonly(),
        hideToast: (toast) => this.hideToast(toast),
      },
    );
    const componentPortal = this.overlayService.createComponentPortal(
      OnyxToasterComponent,
      componentInjector,
    );

    this.overlayService.setOverlayRef(overlayRef);
    this.overlayService.attachToOverlay(componentPortal);
  }
}
